import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Check,
  LogOut,
  Globe,
  MapPin,
  Loader2,
  Moon,
  Home,
  Star,
  Eye,
  Users,
  Music,
  LayoutGrid,
  Palette,
  Type,
  MessageSquare,
  User,
  Crown,
  Sparkles,
} from "lucide-react";
import {
  clampSearchRadiusKm,
  isSearchRadiusAllowed,
  minTierForSearchRadiusKm,
} from "@pulse/shared";
import {
  consumerEntitlementsQueryKey,
  formatConsumerSubscriptionLabel,
  useConsumerEntitlements,
} from "@/lib/consumerEntitlements";
import { RADII, useSettings, type Radius } from "@/lib/settings";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useAuth } from "@/lib/auth";
import { useLocation } from "@/lib/location";
import { trackConsumerGeoActivity } from "@/lib/geoActivityTrack";
import { useStatusMessage } from "@/hooks/useStatusMessage";
import {
  CATEGORY_TAGS,
  MUSIC_STYLE_TAGS,
  NIGHT_STYLE_TAGS,
  interestTagLabel,
  toggleTag,
  useSaveUserPreferences,
  useUserPreferences,
} from "@/lib/userPreferences";
import { onSquadJoined, settingsSquadsQueryKey } from "@/lib/squad";
import {
  RejoinRequestError,
  submitLeftSquadRejoinRequest,
} from "@/lib/rejoinRequests";
import {
  acceptSquadInvite,
  declineSquadInvite,
  SquadInviteError,
} from "@/lib/squadInvites";
import { SquadDissolveFlow } from "@/components/squad/SquadDissolveFlow";
import { SquadLeaveFlow } from "@/components/squad/SquadLeaveFlow";
import { DissolvedSquadRow } from "@/components/squad/DissolvedSquadRow";
import { LeftSquadRow } from "@/components/squad/LeftSquadRow";
import { useDissolvedSquads } from "@/lib/dissolvedSquads";
import { useLeftSquads, type LeftSquad } from "@/lib/leftSquads";
import { supabase } from "@/lib/supabase";
import { emailLocalPart } from "@/lib/profileDisplay";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { FeedbackSheet } from "@/components/FeedbackSheet";
import { type Theme, applyTheme, getStoredTheme } from "@/lib/theme";
import { type FontGroup, applyFontGroup, getStoredFontGroup } from "@/lib/fontGroup";
import { ProfilePersonalDataForm } from "@/components/profile/ProfilePersonalDataForm";
import { NotificationSettingsSection } from "@/components/notifications/NotificationSettingsSection";
import { DeleteAccountModal } from "@/components/account/DeleteAccountModal";
import { AboModal } from "@/components/account/AboModal";
import { SettingsToggle } from "@/components/ui/settings-toggle";
import { CONSUMER_TIER_LABELS, isConsumerTier } from "@/lib/consumerTier";
import {
  consumerSubscriptionQueryKey,
  fetchActiveSubscription,
} from "@/lib/stripe";
import { deleteAccount } from "@/lib/deleteAccount";
import { useNotificationPreferences } from "@/lib/notifications";
import {
  formatProfilePersonalSummary,
  useProfilePersonal,
  useSaveProfilePersonal,
  type ProfileGender,
} from "@/lib/profilePersonal";

export const Route = createFileRoute("/settings")({
  head: () => ({
    meta: [
      { title: "Settings · Pulse" },
      { name: "description", content: "Language, search radius and account settings for Pulse." },
    ],
  }),
  validateSearch: (search: Record<string, unknown>) => ({
    section:
      typeof search.section === "string" && search.section.length > 0
        ? search.section
        : undefined,
  }),
  component: SettingsScreen,
});

type MemoryViewPreference = "squad" | "grid" | "list";
type SettingsSquad = {
  id: string;
  name: string;
  role: "admin" | "member";
  memberCount?: number;
  joinedAt?: string;
};
type SubPage = "musik" | "kategorien" | "vibes" | "profil" | "persoenlich" | null;

const THEME_OPTIONS = [
  { id: "platinum", label: "Premium Black", preview: ["#0A0A0A", "#2C2E32", "#C4C8CE"] },
  { id: "amber", label: "Amber", preview: ["#0A0A0A", "#1a1a14", "#C8B88A"] },
  { id: "velvet", label: "Velvet", preview: ["#080E14", "#0D1520", "#2DD4BF"] },
  { id: "bordeaux", label: "Bordeaux", preview: ["#0A0A0A", "#1a1014", "#5C1A30"] },
  { id: "dusk", label: "Dusk", preview: ["#2a2a32", "#34343c", "#9A9AA4"] },
  { id: "mist", label: "Mist", preview: ["#8A95A8", "#A8B2C2", "#3D4F6E"] },
  { id: "arctic", label: "Arctic", preview: ["#EEF2F6", "#F8FAFC", "#1A1A2E"] },
] as const satisfies ReadonlyArray<{ id: Theme; label: string; preview: readonly [string, string, string] }>;

function themeLabel(theme: Theme): string {
  return THEME_OPTIONS.find((t) => t.id === theme)?.label ?? "Premium Black";
}

function useLocalStorage<T>(key: string, initial: T): [T, (v: T) => void] {
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(key);
      if (raw != null) return JSON.parse(raw) as T;
    } catch {
      /* ignore */
    }
    return initial;
  });

  const set = (v: T) => {
    try {
      localStorage.setItem(key, JSON.stringify(v));
    } catch {
      /* ignore */
    }
    setValue(v);
  };

  return [value, set];
}

function SettingsSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="mb-2 px-1 font-headline text-[10px] font-semibold uppercase tracking-widest text-text-subtle">
        {title}
      </p>
      <div className="divide-y divide-border overflow-hidden rounded-2xl border border-border bg-bg-surface">
        {children}
      </div>
    </div>
  );
}

function SettingsRow({
  icon,
  label,
  value,
  valueColor,
  labelColor,
  onPress,
}: {
  icon?: React.ReactNode;
  label: string;
  value?: string;
  valueColor?: string;
  labelColor?: string;
  onPress?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onPress}
      disabled={!onPress}
      className="flex w-full items-center gap-3 px-4 py-3.5 text-left transition-colors active:bg-bg-surface disabled:cursor-default"
    >
      {icon && (
        <span className="flex h-5 w-5 shrink-0 items-center justify-center text-muted-foreground">
          {icon}
        </span>
      )}
      <span className={cn("flex-1 text-sm", labelColor ?? "text-foreground")}>{label}</span>
      {value && (
        <span className={cn("shrink-0 text-xs", valueColor ?? "text-muted-foreground")}>{value}</span>
      )}
      {onPress && <ChevronRight size={14} className="ml-1 shrink-0 text-text-subtle" />}
    </button>
  );
}


function SettingsExpandableRow({
  settingKey,
  icon,
  label,
  value,
  selectedValue,
  options,
  onSelect,
  expandedSetting,
  onToggle,
}: {
  settingKey: string;
  icon?: React.ReactNode;
  label: string;
  value: string;
  selectedValue?: string;
  options: Array<{
    value: string;
    label: string;
    description?: string;
    disabled?: boolean;
  }>;
  onSelect: (value: string) => void;
  expandedSetting: string | null;
  onToggle: (key: string) => void;
}) {
  const isOpen = expandedSetting === settingKey;
  const current = selectedValue ?? value;

  return (
    <div>
      <button
        type="button"
        onClick={() => onToggle(settingKey)}
        className="flex w-full items-center gap-3 px-4 py-3.5 transition-colors active:bg-bg-surface"
      >
        {icon && (
          <span className="flex h-5 w-5 shrink-0 items-center justify-center text-muted-foreground">
            {icon}
          </span>
        )}
        <span className="flex-1 text-left text-sm text-foreground">{label}</span>
        <span className="mr-1 text-xs text-muted-foreground">{value}</span>
        <ChevronDown
          size={14}
          className={cn("shrink-0 text-text-subtle transition-transform", isOpen && "rotate-180")}
        />
      </button>

      {isOpen && (
        <div className="border-t border-border bg-bg-surface">
          {options.map((opt, i) => (
            <button
              key={opt.value}
              type="button"
              disabled={opt.disabled}
              onClick={() => {
                if (opt.disabled) return;
                onSelect(opt.value);
                onToggle(settingKey);
              }}
              className={cn(
                "flex w-full items-center justify-between px-6 py-3 transition-colors active:bg-bg-surface",
                i < options.length - 1 && "border-b border-border",
                opt.disabled && "cursor-not-allowed opacity-60",
              )}
            >
              <span className="text-left">
                <span
                  className={cn(
                    "block text-sm",
                    opt.value === current ? "text-foreground" : "text-muted-foreground",
                  )}
                >
                  {opt.label}
                </span>
                {opt.description && (
                  <span className="mt-0.5 block text-[10px] text-text-subtle">{opt.description}</span>
                )}
              </span>
              {opt.value === current && <Check size={13} className="shrink-0 text-pink-400" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function SettingsThemeRow({
  settingKey,
  icon,
  label,
  value,
  currentTheme,
  onSelect,
  onLockedSelect,
  themeUnlock,
  expandedSetting,
  onToggle,
}: {
  settingKey: string;
  icon?: React.ReactNode;
  label: string;
  value: string;
  currentTheme: Theme;
  onSelect: (theme: Theme) => void;
  onLockedSelect?: () => void;
  themeUnlock?: "all" | "platinum_only";
  expandedSetting: string | null;
  onToggle: (key: string) => void;
}) {
  const { t } = useTranslation("consumer");
  const isOpen = expandedSetting === settingKey;
  const platinumOnly = themeUnlock === "platinum_only";

  return (
    <div>
      <button
        type="button"
        onClick={() => onToggle(settingKey)}
        className="flex w-full items-center gap-3 px-4 py-3.5 transition-colors active:bg-bg-surface"
      >
        {icon && (
          <span className="flex h-5 w-5 shrink-0 items-center justify-center text-muted-foreground">
            {icon}
          </span>
        )}
        <span className="flex-1 text-left text-sm text-foreground">{label}</span>
        <span className="mr-1 text-xs text-muted-foreground">{value}</span>
        <ChevronDown
          size={14}
          className={cn("shrink-0 text-text-subtle transition-transform", isOpen && "rotate-180")}
        />
      </button>

      {isOpen && (
        <div className="border-t border-border bg-bg-surface">
          {THEME_OPTIONS.map((theme, i) => {
            const isSelected = currentTheme === theme.id;
            const locked = platinumOnly && theme.id !== "platinum";
            return (
              <button
                key={theme.id}
                type="button"
                onClick={() => {
                  if (locked) {
                    onLockedSelect?.();
                    return;
                  }
                  onSelect(theme.id);
                  onToggle(settingKey);
                }}
                className={cn(
                  "flex w-full items-center gap-3 px-6 py-2.5 transition-colors active:bg-bg-surface",
                  i < THEME_OPTIONS.length - 1 && "border-b border-border",
                  locked && "opacity-55",
                )}
              >
                <span
                  className={cn(
                    "flex h-5 w-14 shrink-0 overflow-hidden rounded-md border transition-colors",
                    isSelected ? "border-[var(--accent)]" : "border-transparent",
                  )}
                >
                  {theme.preview.map((color, j) => (
                    <span key={j} className="flex-1" style={{ backgroundColor: color }} />
                  ))}
                </span>
                <span
                  className={cn(
                    "flex-1 text-left text-sm",
                    isSelected ? "text-foreground" : "text-muted-foreground",
                  )}
                >
                  {theme.label}
                  {locked ? (
                    <span className="ml-2 text-[10px] uppercase tracking-wider text-text-subtle">
                      {t("entitlements.themeLocked")}
                    </span>
                  ) : null}
                </span>
                {isSelected && !locked ? <Check size={13} className="shrink-0 text-pink-400" /> : null}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function SettingsSquadRow({
  squad,
  isOpen,
  onToggle,
  onLeave,
  onDissolve,
}: {
  squad: SettingsSquad;
  isOpen: boolean;
  onToggle: () => void;
  onLeave: (squad: SettingsSquad) => void;
  onDissolve: (squad: SettingsSquad) => void;
}) {
  const { t } = useTranslation("consumer");
  const roleLabel = squad.role === "admin" ? t("settings.roleAdmin") : t("settings.roleMember");
  const roleColor = squad.role === "admin" ? "text-amber-400/70" : "text-muted-foreground";

  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center gap-3 px-4 py-3.5 transition-colors active:bg-bg-surface"
      >
        <span className="flex h-5 w-5 shrink-0 items-center justify-center text-[10px] text-muted-foreground">
          ✦
        </span>
        <span className="flex-1 text-left text-sm text-foreground">{squad.name}</span>
        <span className={cn("shrink-0 text-xs", roleColor)}>{roleLabel}</span>
        <ChevronDown
          size={14}
          className={cn("ml-1 shrink-0 text-text-subtle transition-transform", isOpen && "rotate-180")}
        />
      </button>

      {isOpen && (
        <div className="border-t border-border bg-bg-surface px-4 py-3">
          <div className="mb-3 divide-y divide-border overflow-hidden rounded-xl border border-border">
            <div className="flex items-center justify-between px-4 py-3">
              <span className="text-sm text-muted-foreground">{t("settings.members")}</span>
              <span className="text-sm text-muted-foreground">{squad.memberCount ?? "—"}</span>
            </div>
            <div className="flex items-center justify-between px-4 py-3">
              <span className="text-sm text-muted-foreground">{t("settings.joined")}</span>
              <span className="text-sm text-muted-foreground">
                {squad.joinedAt
                  ? new Date(squad.joinedAt).toLocaleDateString("de-CH")
                  : "—"}
              </span>
            </div>
          </div>

          {squad.role === "admin" && (
            <button
              type="button"
              className="mb-2 flex w-full items-center rounded-xl border border-border bg-bg-surface px-4 py-3 text-left text-sm text-muted-foreground transition-colors active:bg-bg-surface-hover"
            >
              {/* TODO: Admin übergeben — no handler yet */}
              {t("settings.transferAdmin")}
            </button>
          )}

          <button
            type="button"
            onClick={() => (squad.role === "admin" ? onDissolve(squad) : onLeave(squad))}
            className="flex w-full items-center rounded-xl border border-red-500/10 bg-red-500/5 px-4 py-3 text-left text-sm text-red-400/60 transition-colors active:bg-red-500/10"
          >
            {squad.role === "admin" ? t("settings.dissolveSquad") : t("settings.leaveSquad")}
          </button>
        </div>
      )}
    </div>
  );
}

const DISPLAY_NAME_MAX_LENGTH = 80;

function PersonalDataEditSection() {
  const { t } = useTranslation("consumer");
  const { data, isLoading } = useProfilePersonal();
  const { canSave, isSaving, saveError, save } = useSaveProfilePersonal();
  const [birthDate, setBirthDate] = useState("");
  const [gender, setGender] = useState<ProfileGender | null>(null);
  const [status, setStatus] = useState<string | null>(null);

  useEffect(() => {
    if (data) {
      setBirthDate(data.birthDate ?? "");
      setGender(data.gender);
    }
  }, [data]);

  async function handleSave() {
    setStatus(null);
    try {
      await save({ birthDate: birthDate || null, gender });
      toast.success(t("settings.personalDataSaved"));
    } catch (err) {
      setStatus(err instanceof Error ? err.message : t("settings.saveFailed"));
    }
  }

  if (isLoading) {
    return <p className="px-5 py-6 text-center text-sm text-muted-foreground">{t("settings.loading")}</p>;
  }

  return (
    <div className="px-5 py-6">
      <ProfilePersonalDataForm
        birthDate={birthDate}
        gender={gender}
        onBirthDateChange={setBirthDate}
        onGenderChange={setGender}
        showHint
        className="mb-6"
      />

      {canSave && (
        <button
          type="button"
          onClick={() => void handleSave()}
          disabled={isSaving}
          className="flex w-full items-center justify-center gap-2 rounded-xl border border-pink-500/20 bg-pink-500/15 py-3 text-sm font-medium text-pink-300 disabled:opacity-60"
        >
          {isSaving && <Loader2 className="h-4 w-4 animate-spin" />}
          {t("settings.save")}
        </button>
      )}

      {(saveError || status) && (
        <p className="mt-4 text-center text-xs text-red-400/70">
          {status ?? (saveError instanceof Error ? saveError.message : t("settings.saveFailed"))}
        </p>
      )}
    </div>
  );
}

function ProfileEditSection({
  initialName,
  email,
  onSaved,
}: {
  initialName: string;
  email?: string;
  onSaved: () => Promise<void>;
}) {
  const { t } = useTranslation("consumer");
  const [displayName, setDisplayName] = useState(initialName);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDisplayName(initialName);
  }, [initialName]);

  async function handleSave() {
    const trimmed = displayName.trim();
    if (!trimmed) {
      setError(t("settings.displayNameRequired"));
      return;
    }
    if (trimmed.length > DISPLAY_NAME_MAX_LENGTH) {
      setError(t("settings.maxChars", { max: DISPLAY_NAME_MAX_LENGTH }));
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const derivedSlug = trimmed
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ".")
        .replace(/^\.+|\.+$/g, "")
        .slice(0, 40);
      const slug = emailLocalPart(email) ?? (derivedSlug || "user");

      const { error: updateError } = await supabase.auth.updateUser({
        data: {
          full_name: trimmed,
          name: slug,
        },
      });
      if (updateError) throw updateError;

      await onSaved();
      toast.success(t("settings.profileSaved"));
    } catch (err) {
      setError(err instanceof Error ? err.message : t("settings.saveFailed"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="px-5 py-6">
      <label className="mb-2 block text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
        {t("settings.displayName")}
      </label>
      <input
        value={displayName}
        onChange={(e) => setDisplayName(e.target.value)}
        maxLength={DISPLAY_NAME_MAX_LENGTH}
        placeholder={t("settings.displayNamePlaceholder")}
        className="mb-4 w-full rounded-2xl border border-border bg-bg-surface px-4 py-3 text-sm text-foreground outline-none focus:border-pulse"
      />
      <button
        type="button"
        onClick={() => void handleSave()}
        disabled={saving}
        className="flex w-full items-center justify-center gap-2 rounded-xl border border-pink-500/20 bg-pink-500/15 py-3 text-sm font-medium text-pink-300 disabled:opacity-60"
      >
        {saving && <Loader2 className="h-4 w-4 animate-spin" />}
        {t("settings.save")}
      </button>
      {error && <p className="mt-4 text-center text-xs text-red-400/70">{error}</p>}
    </div>
  );
}

function SettingsScreen() {
  const { t } = useTranslation("consumer");
  const { section: focusSection } = Route.useSearch();
  const languageRegionRef = useRef<HTMLDivElement>(null);
  const router = useRouter();
  const queryClient = useQueryClient();
  const { radius, setRadius } = useSettings();
  const { entitlements, tier, accessKind, subscriptionSource, isLoading: entitlementsLoading } =
    useConsumerEntitlements();
  const { user, signOut, isGuest, refreshSession, hydrated } = useAuth();
  const {
    preferences: notificationPreferences,
    isLoading: notificationPrefsLoading,
    save: saveNotificationPreferences,
  } = useNotificationPreferences({
    userId: user?.id,
    enabled: !isGuest && !!user?.id,
  });
  const { location } = useLocation();
  const [signingOut, setSigningOut] = useState(false);
  const { message: signOutError, setMessage: setSignOutError } = useStatusMessage(3500);
  const { data: preferences, isLoading: prefsLoading } = useUserPreferences();
  const { data: profilePersonal } = useProfilePersonal();
  const { canSave, isSaving, saveError, save } = useSaveUserPreferences();
  const { message: prefsStatus, setMessage: setPrefsStatus } = useStatusMessage(2500);

  const [musicStyles, setMusicStyles] = useState<string[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [nightStyles, setNightStyles] = useState<string[]>([]);
  const [dislikeNote, setDislikeNote] = useState("");

  const [memoriesView, setMemoriesView] = useLocalStorage<MemoryViewPreference>(
    "pulse.settings.memoriesView",
    "squad",
  );
  const [startTab, setStartTab] = useLocalStorage<string>("pulse.settings.startTab", "Entdecken");
  const [venueFilter, setVenueFilter] = useLocalStorage<string>(
    "pulse.settings.venueFilter",
    "Alle",
  );
  const [profilePublic, setProfilePublic] = useLocalStorage("pulse.settings.profilePublic", true);
  const [memoriesPublic, setMemoriesPublic] = useLocalStorage(
    "pulse.settings.memoriesPublic",
    true,
  );

  const [expandedSetting, setExpandedSetting] = useState<string | null>(null);
  const [expandedSquadId, setExpandedSquadId] = useState<string | null>(null);
  const [dissolveSquadId, setDissolveSquadId] = useState<string | null>(null);
  const [leaveSquadId, setLeaveSquadId] = useState<string | null>(null);
  const [subPage, setSubPage] = useState<SubPage>(null);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [deleteAccountOpen, setDeleteAccountOpen] = useState(false);
  const [aboModalOpen, setAboModalOpen] = useState(false);
  const [currentTheme, setCurrentTheme] = useState<Theme>(() => getStoredTheme());
  const [fontGroup, setFontGroup] = useState<FontGroup>(() => getStoredFontGroup());
  const checkoutHandledRef = useRef(false);

  function toggleExpand(key: string) {
    setExpandedSetting((prev) => (prev === key ? null : key));
  }

  function toggleSquadExpand(squadId: string) {
    setExpandedSquadId((prev) => (prev === squadId ? null : squadId));
  }

  function handleThemeChange(theme: Theme) {
    if (entitlements?.themeUnlock === "platinum_only" && theme !== "platinum") {
      setAboModalOpen(true);
      return;
    }
    applyTheme(theme);
    setCurrentTheme(theme);
  }

  function handleFontGroupChange(group: FontGroup) {
    applyFontGroup(group);
    setFontGroup(group);
  }

  const subscriptionLabel = formatConsumerSubscriptionLabel(tier, accessKind, subscriptionSource, {
    tierLabels: CONSUMER_TIER_LABELS,
    noSubscription: t("settings.noSubscription"),
    founderAccess: t("settings.founderAccess"),
    internalAccess: t("settings.internalAccess"),
    insiderAccess: t("settings.insiderAccess"),
    earlybirdAccess: t("settings.earlybirdAccess"),
    trialAccess: t("settings.trialAccess"),
  });

  useEffect(() => {
    if (!entitlements) return;
    const clamped = clampSearchRadiusKm(radius, entitlements) as Radius;
    if (clamped !== radius) setRadius(clamped);
  }, [entitlements, radius, setRadius]);

  const radiusOptions = RADII.map((r) => {
    const allowed = entitlements ? isSearchRadiusAllowed(r, entitlements) : true;
    const minTier = minTierForSearchRadiusKm(r);
    return {
      value: String(r),
      label: `${r} km`,
      disabled: !allowed,
      description: !allowed
        ? t("settings.searchRadiusUpsell", { tier: CONSUMER_TIER_LABELS[minTier] })
        : undefined,
    };
  });

  useEffect(() => {
    if (focusSection !== "languageRegion") return;
    const id = window.setTimeout(() => {
      languageRegionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 150);
    return () => window.clearTimeout(id);
  }, [focusSection]);

  useEffect(() => {
    if (typeof window === "undefined" || checkoutHandledRef.current) return;
    const params = new URLSearchParams(window.location.search);
    const checkout = params.get("checkout");
    if (checkout !== "success" && checkout !== "canceled") return;
    if (checkout === "success" && (!hydrated || !user?.id || isGuest)) return;

    checkoutHandledRef.current = true;

    void (async () => {
      try {
        if (checkout === "success" && user?.id) {
          trackConsumerGeoActivity("subscription");
          try {
            await queryClient.invalidateQueries({
              queryKey: consumerSubscriptionQueryKey(user.id),
            });
            await queryClient.invalidateQueries({
              queryKey: consumerEntitlementsQueryKey(user.id),
            });
            const sub = await fetchActiveSubscription("user", user.id);
            queryClient.setQueryData(consumerSubscriptionQueryKey(user.id), sub);
            const tierLabel =
              sub?.tier && isConsumerTier(sub.tier) ? CONSUMER_TIER_LABELS[sub.tier] : null;
            toast.success(
              tierLabel
                ? t("subscription.checkoutSuccess", { tier: tierLabel })
                : t("subscription.checkoutSuccessGeneric"),
            );
          } catch {
            toast.success(t("subscription.checkoutSuccessGeneric"));
          }
        } else if (checkout === "canceled") {
          toast.message(t("subscription.checkoutCanceled"));
        }
      } finally {
        await router.navigate({ to: "/settings", replace: true });
      }
    })();
  }, [hydrated, user?.id, isGuest, queryClient, router, t]);
  const { data: squads = [] } = useQuery({
    queryKey: settingsSquadsQueryKey(user?.id ?? "none"),
    queryFn: async (): Promise<SettingsSquad[]> => {
      const userId = user!.id!;
      const { data: memberships, error } = await supabase
        .from("squad_members")
        .select("role, squad_id, joined_at, squads(id, name, created_by)")
        .eq("user_id", userId)
        .in("status", ["active", "invited"]);
      if (error) throw error;

      const map = new Map<string, SettingsSquad>();
      for (const row of memberships ?? []) {
        const squad = row.squads as { id: string; name: string; created_by: string } | null;
        if (!squad) continue;
        const role =
          row.role === "admin" || squad.created_by === userId ? "admin" : "member";
        map.set(squad.id, {
          id: squad.id,
          name: squad.name,
          role,
          joinedAt: row.joined_at,
        });
      }

      const { data: created } = await supabase
        .from("squads")
        .select("id, name, created_at")
        .eq("created_by", userId)
        .eq("status", "active");

      for (const squad of created ?? []) {
        if (!map.has(squad.id)) {
          map.set(squad.id, {
            id: squad.id,
            name: squad.name,
            role: "admin",
            joinedAt: squad.created_at ?? undefined,
          });
        }
      }

      const squadIds = [...map.keys()];
      if (squadIds.length > 0) {
        const { data: allMembers } = await supabase
          .from("squad_members")
          .select("squad_id")
          .in("squad_id", squadIds)
          .eq("status", "active");

        const counts = new Map<string, number>();
        for (const m of allMembers ?? []) {
          counts.set(m.squad_id, (counts.get(m.squad_id) ?? 0) + 1);
        }
        for (const [id, squad] of map) {
          map.set(id, { ...squad, memberCount: counts.get(id) ?? 0 });
        }
      }

      return [...map.values()].sort((a, b) => a.name.localeCompare(b.name));
    },
    enabled: !!user?.id && !isGuest,
  });
  const { dissolvedSquads, dismissDissolvedSquad } = useDissolvedSquads();
  const { leftSquads, dismissLeftSquad } = useLeftSquads(user?.id);
  const activeSquadIds = new Set(squads.map((s) => s.id));
  const leftGhostSquads = leftSquads.filter((g) => !activeSquadIds.has(g.id));
  const ghostSquads = dissolvedSquads.filter((g) => !activeSquadIds.has(g.id));

  useEffect(() => {
    if (preferences) {
      setMusicStyles(preferences.musicStyles);
      setCategories(preferences.categories);
      setNightStyles(preferences.nightStyles);
      setDislikeNote(preferences.dislikeNote ?? "");
    }
  }, [preferences]);

  const name = user?.name;
  const email = user?.email;
  const username = email?.split("@")[0] ?? name?.toLowerCase().replace(/\s+/g, "") ?? "pulse";
  const city = location ?? "Zurich";
  const memoriesViewLabel =
    memoriesView === "squad"
      ? t("settings.memoriesViewOptions.squad")
      : memoriesView === "grid"
        ? t("settings.memoriesViewOptions.grid")
        : t("settings.memoriesViewOptions.list");
  // Stored values (startTab / venueFilter) stay unchanged; only the display label is localised.
  const startTabLabel = (v: string) =>
    v === "Entdecken"
      ? t("settings.startTabOptions.discover")
      : v === "MyNight"
        ? t("settings.startTabOptions.myNight")
        : v === "Karte"
          ? t("settings.startTabOptions.map")
          : v;
  const venueFilterLabel = (v: string) =>
    v === "Alle"
      ? t("settings.venueFilterOptions.all")
      : v === "New"
        ? t("settings.venueFilterOptions.new")
        : v === "Top Pick"
          ? t("settings.venueFilterOptions.topPick")
          : v === "Populär"
            ? t("settings.venueFilterOptions.popular")
            : v;

  const handleSignOut = async () => {
    setSigningOut(true);
    setSignOutError(null);
    try {
      const error = await signOut();
      if (error) setSignOutError(error);
    } finally {
      setSigningOut(false);
    }
  };

  const handleSavePreferences = async () => {
    try {
      await save({
        musicStyles,
        categories,
        maxBudget: preferences?.maxBudget ?? 50,
        nightStyles,
        dislikeNote: dislikeNote.trim() || null,
        preferredLanguage: preferences?.preferredLanguage ?? null,
      });
      setPrefsStatus(t("settings.interestsSaved"));
      setSubPage(null);
    } catch {
      /* saveError surfaced below */
    }
  };

  const handleOpenLeave = (squad: SettingsSquad) => {
    setLeaveSquadId(squad.id);
  };

  const handleOpenDissolve = (squad: SettingsSquad) => {
    setDissolveSquadId(squad.id);
  };

  const handleRequestRejoin = async (leftSquad: LeftSquad) => {
    if (!user?.id) return;
    try {
      const { requestId, alreadyPending } = await submitLeftSquadRejoinRequest(leftSquad);
      if (!requestId) {
        toast.error(t("settings.rejoinFailed"));
        return;
      }
      toast.success(alreadyPending ? t("settings.rejoinAlreadyPending") : t("settings.rejoinSent"));
    } catch (err) {
      if (err instanceof RejoinRequestError) {
        if (err.code === "squad_not_found") {
          toast.info(t("settings.squadGone"));
          return;
        }
        toast.error(err.message);
        return;
      }
      toast.error(err instanceof Error ? err.message : t("settings.rejoinFailed"));
    }
  };

  const handleAcceptInvite = async (leftSquad: LeftSquad) => {
    if (!user?.id) return;
    try {
      const { squadId, squadName } = await acceptSquadInvite(leftSquad.id);
      await onSquadJoined(queryClient, user.id, squadId);
      toast.success(t("settings.rejoinedSquad", { name: squadName }));
      router.navigate({ to: "/squad/$id", params: { id: squadId } });
    } catch (err) {
      toast.error(err instanceof SquadInviteError ? err.message : t("settings.acceptFailed"));
    }
  };

  const handleDeclineInvite = async (leftSquad: LeftSquad) => {
    try {
      await declineSquadInvite(leftSquad.id);
      toast.info(t("settings.inviteDeclined"));
    } catch (err) {
      toast.error(err instanceof SquadInviteError ? err.message : t("settings.declineFailed"));
    }
  };

  const handleDeleteAccount = async () => {
    await deleteAccount();
    queryClient.clear();
    await supabase.auth.signOut();
    setDeleteAccountOpen(false);
    toast.success(t("settings.accountDeleted"));
    await router.navigate({ to: "/" });
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="flex items-center gap-3 px-5 pb-6 pt-14">
        <button
          type="button"
          onClick={() => router.history.back()}
          className="text-muted-foreground"
          aria-label="Back"
        >
          <ChevronLeft size={20} />
        </button>
        <h1 className="text-xl font-bold text-foreground">{t("settings.title")}</h1>
      </div>

      <div className="mb-8 px-5">
        <div className="flex items-center gap-4 rounded-2xl border border-border bg-bg-surface px-4 py-4">
          <div className="h-14 w-14 shrink-0 overflow-hidden rounded-full bg-bg-surface-hover">
            <div className="flex h-full w-full items-center justify-center text-xl font-bold text-muted-foreground">
              {name?.charAt(0) ?? email?.charAt(0) ?? "?"}
            </div>
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate font-semibold text-foreground">{name ?? email ?? t("settings.guest")}</p>
            <p className="truncate text-sm text-muted-foreground">
              @{username} · {city}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setSubPage("profil")}
            className="shrink-0 text-muted-foreground"
            aria-label="Edit profile"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      <div className="space-y-6 px-5">
        <SettingsSection title={t("settings.sections.personalization")}>
          <SettingsThemeRow
            settingKey="theme"
            icon={<Palette size={14} />}
            label={t("settings.theme")}
            value={themeLabel(currentTheme)}
            currentTheme={currentTheme}
            onSelect={handleThemeChange}
            onLockedSelect={() => setAboModalOpen(true)}
            themeUnlock={entitlements?.themeUnlock}
            expandedSetting={expandedSetting}
            onToggle={toggleExpand}
          />
          <SettingsExpandableRow
            settingKey="fontGroup"
            icon={<Type size={14} />}
            label={t("settings.fontStyle")}
            value={
              fontGroup === "premium"
                ? t("settings.fontStyleOptions.premium")
                : t("settings.fontStyleOptions.default")
            }
            selectedValue={fontGroup}
            options={[
              {
                value: "default",
                label: t("settings.fontStyleOptions.default"),
                description: t("settings.fontStyleOptions.defaultDesc"),
              },
              {
                value: "premium",
                label: t("settings.fontStyleOptions.premium"),
                description: t("settings.fontStyleOptions.premiumDesc"),
              },
            ]}
            onSelect={(v) => handleFontGroupChange(v as FontGroup)}
            expandedSetting={expandedSetting}
            onToggle={toggleExpand}
          />
          <SettingsExpandableRow
            settingKey="memoriesView"
            icon={<Moon size={14} />}
            label={t("settings.memoriesView")}
            value={memoriesViewLabel}
            selectedValue={memoriesView}
            options={[
              { value: "squad", label: t("settings.memoriesViewOptions.squad"), description: t("settings.memoriesViewOptions.squadDesc") },
              { value: "grid", label: t("settings.memoriesViewOptions.grid"), description: t("settings.memoriesViewOptions.gridDesc") },
              { value: "list", label: t("settings.memoriesViewOptions.list"), description: t("settings.memoriesViewOptions.listDesc") },
            ]}
            onSelect={(v) => setMemoriesView(v as MemoryViewPreference)}
            expandedSetting={expandedSetting}
            onToggle={toggleExpand}
          />
          <SettingsExpandableRow
            settingKey="startTab"
            icon={<Home size={14} />}
            label={t("settings.startTab")}
            value={startTabLabel(startTab)}
            selectedValue={startTab}
            options={[
              { value: "Entdecken", label: t("settings.startTabOptions.discover"), description: t("settings.startTabOptions.discoverDesc") },
              { value: "MyNight", label: t("settings.startTabOptions.myNight"), description: t("settings.startTabOptions.myNightDesc") },
              { value: "Karte", label: t("settings.startTabOptions.map"), description: t("settings.startTabOptions.mapDesc") },
            ]}
            onSelect={setStartTab}
            expandedSetting={expandedSetting}
            onToggle={toggleExpand}
          />
          <SettingsExpandableRow
            settingKey="venueFilter"
            icon={<Star size={14} />}
            label={t("settings.venueFilter")}
            value={venueFilterLabel(venueFilter)}
            selectedValue={venueFilter}
            options={[
              { value: "Alle", label: t("settings.venueFilterOptions.all"), description: t("settings.venueFilterOptions.allDesc") },
              { value: "New", label: t("settings.venueFilterOptions.new"), description: t("settings.venueFilterOptions.newDesc") },
              { value: "Top Pick", label: t("settings.venueFilterOptions.topPick"), description: t("settings.venueFilterOptions.topPickDesc") },
              { value: "Populär", label: t("settings.venueFilterOptions.popular"), description: t("settings.venueFilterOptions.popularDesc") },
            ]}
            onSelect={setVenueFilter}
            expandedSetting={expandedSetting}
            onToggle={toggleExpand}
          />
        </SettingsSection>

        {!isGuest && (
          <SettingsSection title={t("settings.sections.squads")}>
            {squads.length === 0 && leftGhostSquads.length === 0 && ghostSquads.length === 0 ? (
              <SettingsRow label={t("settings.noSquads")} />
            ) : (
              <>
                {squads.map((squad) => (
                  <SettingsSquadRow
                    key={squad.id}
                    squad={squad}
                    isOpen={expandedSquadId === squad.id}
                    onToggle={() => toggleSquadExpand(squad.id)}
                    onLeave={handleOpenLeave}
                    onDissolve={handleOpenDissolve}
                  />
                ))}
                {leftGhostSquads.map((g) => (
                  <LeftSquadRow
                    key={g.id}
                    squad={g}
                    onDismiss={dismissLeftSquad}
                    onRequestRejoin={(s) => void handleRequestRejoin(s)}
                    onAcceptInvite={(s) => void handleAcceptInvite(s)}
                    onDeclineInvite={(s) => void handleDeclineInvite(s)}
                    variant="settings"
                  />
                ))}
                {ghostSquads.map((g) => (
                  <DissolvedSquadRow
                    key={g.id}
                    squad={g}
                    onDismiss={dismissDissolvedSquad}
                    variant="settings"
                  />
                ))}
              </>
            )}
          </SettingsSection>
        )}

        <div ref={languageRegionRef}>
          <SettingsSection title={t("settings.sections.languageRegion")}>
          <div className="flex w-full items-center gap-3 px-4 py-3.5">
            <div className="flex h-5 w-5 shrink-0 items-center justify-center text-muted-foreground">
              <Globe size={14} />
            </div>
            <span className="flex-1 text-sm text-foreground">{t("settings.language")}</span>
            <LanguageSwitcher />
          </div>
          <SettingsExpandableRow
            settingKey="radius"
            icon={<MapPin size={14} />}
            label={t("settings.searchRadius")}
            value={`${radius} km`}
            selectedValue={String(radius)}
            options={radiusOptions}
            onSelect={(v) => setRadius(Number(v) as Radius)}
            expandedSetting={expandedSetting}
            onToggle={toggleExpand}
          />
        </SettingsSection>
        </div>

        <SettingsSection title={t("settings.sections.preferences")}>
          <SettingsRow
            icon={<Sparkles size={14} />}
            label={t("settings.yourVibes")}
            value={t("settings.selectedCount", {
              count: musicStyles.length + categories.length + nightStyles.length,
            })}
            onPress={() => setSubPage("vibes")}
          />
          <SettingsRow
            icon={<Music size={14} />}
            label={t("settings.music")}
            value={t("settings.selectedCount", { count: musicStyles.length })}
            onPress={() => setSubPage("musik")}
          />
          <SettingsRow
            icon={<LayoutGrid size={14} />}
            label={t("settings.categories")}
            value={t("settings.selectedCount", { count: categories.length })}
            onPress={() => setSubPage("kategorien")}
          />
        </SettingsSection>

        {!isGuest && (
          <SettingsSection title={t("settings.sections.personalData")}>
            <SettingsRow
              icon={<User size={14} />}
              label={t("settings.birthGender")}
              value={formatProfilePersonalSummary(profilePersonal, t)}
              onPress={() => setSubPage("persoenlich")}
            />
          </SettingsSection>
        )}

        {!isGuest && user?.id && (
          <SettingsSection title={t("settings.sections.notifications")}>
            <NotificationSettingsSection
              mode="consumer"
              embedded
              userId={user.id}
              preferences={notificationPreferences}
              isLoading={notificationPrefsLoading}
              onSave={saveNotificationPreferences}
            />
          </SettingsSection>
        )}

        {isGuest && (
          <SettingsSection title={t("settings.sections.notifications")}>
            <p className="px-4 py-3.5 text-sm text-muted-foreground">
              {t("settings.signInNotifications")}
            </p>
          </SettingsSection>
        )}

        <SettingsSection title={t("settings.sections.privacy")}>
          <SettingsToggle
            icon={<Eye size={14} />}
            label={t("settings.profilePublic")}
            value={profilePublic}
            onChange={setProfilePublic}
            offClassName="bg-bg-surface-hover"
          />
          <SettingsToggle
            icon={<Users size={14} />}
            label={t("settings.memoriesVisibleFriends")}
            value={memoriesPublic}
            onChange={setMemoriesPublic}
            offClassName="bg-bg-surface-hover"
          />
          <div className="border-t border-border px-4 py-3.5">
            <p className="text-xs leading-relaxed text-muted-foreground">
              {t("settings.privacyNote")}
            </p>
          </div>
        </SettingsSection>

        <SettingsSection title={t("settings.sections.help")}>
          <SettingsRow
            icon={<MessageSquare size={14} />}
            label={t("settings.feedbackSupport")}
            onPress={() => setFeedbackOpen(true)}
          />
        </SettingsSection>

        {!isGuest && user?.id && (
          <SettingsSection title={t("settings.sections.subscription")}>
            <SettingsRow
              icon={<Crown size={14} />}
              label={t("settings.manageSubscription")}
              value={
                entitlementsLoading ? t("settings.loadingSubscription") : subscriptionLabel
              }
              onPress={() => setAboModalOpen(true)}
            />
          </SettingsSection>
        )}

        {user && (
          <SettingsSection title={t("settings.sections.account")}>
            <SettingsRow
              icon={
                signingOut ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <LogOut size={14} />
                )
              }
              label={
                signingOut
                  ? t("settings.signingOut")
                  : user.provider === "guest"
                    ? t("settings.exitGuest")
                    : t("settings.signOut")
              }
              labelColor="text-red-400/70"
              onPress={() => void handleSignOut()}
            />
          </SettingsSection>
        )}

        {!isGuest && user?.email && (
          <div className="pb-2">
            <button
              type="button"
              onClick={() => setDeleteAccountOpen(true)}
              className="rounded-xl border border-red-500/20 bg-red-500/5 px-4 py-2.5 text-sm font-semibold text-red-400/80 transition-colors hover:bg-red-500/10"
            >
              {t("settings.deleteAccount")}
            </button>
            <p className="mt-3 px-1 text-xs leading-relaxed text-muted-foreground">
              {t("settings.deleteAccountNote")}
            </p>
          </div>
        )}

        {(signOutError || prefsStatus) && (
          <p
            className={cn(
              "text-center text-xs",
              signOutError ? "text-red-400/70" : "text-muted-foreground",
            )}
          >
            {signOutError ?? prefsStatus}
          </p>
        )}
      </div>

      <div className="h-24" />

      {subPage && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-background">
          <div className="flex items-center gap-3 border-b border-border px-5 pb-6 pt-14">
            <button
              type="button"
              onClick={() => setSubPage(null)}
              className="text-muted-foreground"
              aria-label="Back"
            >
              <ChevronLeft size={20} />
            </button>
            <h2 className="font-headline text-lg font-bold text-foreground">
              {subPage === "musik"
                ? t("settings.subPageMusic")
                : subPage === "kategorien"
                  ? t("settings.subPageCategories")
                  : subPage === "vibes"
                    ? t("settings.subPageVibes")
                  : subPage === "persoenlich"
                    ? t("settings.subPagePersonal")
                    : t("settings.subPageProfile")}
            </h2>
          </div>

          {subPage === "vibes" && (
            <div className="px-5 py-6">
              {prefsLoading ? (
                <p className="text-center text-sm text-muted-foreground">{t("settings.loading")}</p>
              ) : (
                <>
                  <p className="mb-3 text-xs text-muted-foreground">{t("settings.chooseMusic")}</p>
                  <div className="mb-6 flex flex-wrap gap-2">
                    {MUSIC_STYLE_TAGS.map((genre) => (
                      <button
                        key={genre}
                        type="button"
                        onClick={() => setMusicStyles((prev) => toggleTag(prev, genre))}
                        className={cn(
                          "rounded-full border px-3 py-1.5 text-xs font-medium transition-colors",
                          musicStyles.includes(genre)
                            ? "border-pink-500/30 bg-pink-500/20 text-pink-300"
                            : "border-border bg-bg-surface text-muted-foreground",
                        )}
                      >
                        {interestTagLabel(genre)}
                      </button>
                    ))}
                  </div>
                  <p className="mb-3 text-xs text-muted-foreground">{t("settings.categories")}</p>
                  <div className="mb-6 flex flex-wrap gap-2">
                    {CATEGORY_TAGS.map((cat) => (
                      <button
                        key={cat}
                        type="button"
                        onClick={() => setCategories((prev) => toggleTag(prev, cat))}
                        className={cn(
                          "rounded-full border px-3 py-1.5 text-xs font-medium transition-colors",
                          categories.includes(cat)
                            ? "border-pink-500/30 bg-pink-500/20 text-pink-300"
                            : "border-border bg-bg-surface text-muted-foreground",
                        )}
                      >
                        {cat}
                      </button>
                    ))}
                  </div>
                  <p className="mb-3 text-xs text-muted-foreground">{t("settings.nightStyles")}</p>
                  <div className="mb-6 flex flex-wrap gap-2">
                    {NIGHT_STYLE_TAGS.map((tag) => (
                      <button
                        key={tag}
                        type="button"
                        onClick={() => setNightStyles((prev) => toggleTag(prev, tag))}
                        className={cn(
                          "rounded-full border px-3 py-1.5 text-xs font-medium transition-colors",
                          nightStyles.includes(tag)
                            ? "border-pink-500/30 bg-pink-500/20 text-pink-300"
                            : "border-border bg-bg-surface text-muted-foreground",
                        )}
                      >
                        {t(`onboarding.nightStyles.${tag}`)}
                      </button>
                    ))}
                  </div>
                  <label className="mb-6 block">
                    <span className="text-xs text-muted-foreground">{t("settings.dislikeNote")}</span>
                    <input
                      value={dislikeNote}
                      onChange={(e) => setDislikeNote(e.target.value)}
                      className="mt-2 w-full rounded-xl border border-border bg-bg-surface px-3 py-2.5 text-sm"
                      placeholder={t("onboarding.dislikePlaceholder")}
                    />
                  </label>
                  {canSave && (
                    <button
                      type="button"
                      onClick={() => void handleSavePreferences()}
                      disabled={isSaving}
                      className="w-full rounded-2xl bg-gradient-pulse py-3 text-sm font-bold text-pulse-foreground shadow-pulse disabled:opacity-60"
                    >
                      {isSaving ? t("settings.saving") : t("settings.save")}
                    </button>
                  )}
                  {saveError && (
                    <p className="mt-2 text-center text-xs text-red-400/70">
                      {saveError instanceof Error ? saveError.message : t("settings.saveFailed")}
                    </p>
                  )}
                </>
              )}
            </div>
          )}

          {subPage === "profil" && !isGuest && (
            <ProfileEditSection
              initialName={name ?? email ?? ""}
              email={email}
              onSaved={refreshSession}
            />
          )}

          {subPage === "profil" && isGuest && (
            <div className="px-5 py-6">
              <p className="text-sm text-muted-foreground">
                {t("settings.editProfileSignIn")}
              </p>
            </div>
          )}

          {subPage === "persoenlich" && !isGuest && <PersonalDataEditSection />}

          {subPage === "persoenlich" && isGuest && (
            <div className="px-5 py-6">
              <p className="text-sm text-muted-foreground">
                {t("settings.personalDataSignIn")}
              </p>
            </div>
          )}

          {subPage === "musik" && (
            <div className="px-5 py-6">
              {prefsLoading ? (
                <p className="text-center text-sm text-muted-foreground">{t("settings.loading")}</p>
              ) : (
                <>
                  <p className="mb-4 text-xs text-muted-foreground">{t("settings.chooseMusic")}</p>
                  <div className="mb-6 flex flex-wrap gap-2">
                    {MUSIC_STYLE_TAGS.map((genre) => (
                      <button
                        key={genre}
                        type="button"
                        onClick={() => setMusicStyles((prev) => toggleTag(prev, genre))}
                        className={cn(
                          "rounded-full border px-3 py-1.5 text-xs font-medium transition-colors",
                          musicStyles.includes(genre)
                            ? "border-pink-500/30 bg-pink-500/20 text-pink-300"
                            : "border-border bg-bg-surface text-muted-foreground",
                        )}
                      >
                        {interestTagLabel(genre)}
                      </button>
                    ))}
                  </div>
                  {canSave && (
                    <button
                      type="button"
                      onClick={() => void handleSavePreferences()}
                      disabled={isSaving}
                      className="flex w-full items-center justify-center gap-2 rounded-xl border border-pink-500/20 bg-pink-500/15 py-3 text-sm font-medium text-pink-300 disabled:opacity-60"
                    >
                      {isSaving && <Loader2 className="h-4 w-4 animate-spin" />}
                      {t("settings.save")}
                    </button>
                  )}
                  {(saveError || prefsStatus) && (
                    <p
                      className={cn(
                        "mt-4 text-center text-xs",
                        saveError ? "text-red-400/70" : "text-muted-foreground",
                      )}
                    >
                      {saveError instanceof Error ? saveError.message : prefsStatus}
                    </p>
                  )}
                </>
              )}
            </div>
          )}

          {subPage === "kategorien" && (
            <div className="px-5 py-6">
              {prefsLoading ? (
                <p className="text-center text-sm text-muted-foreground">{t("settings.loading")}</p>
              ) : (
                <>
                  <p className="mb-4 text-xs text-muted-foreground">{t("settings.chooseVenues")}</p>
                  <div className="mb-6 flex flex-wrap gap-2">
                    {CATEGORY_TAGS.map((cat) => (
                      <button
                        key={cat}
                        type="button"
                        onClick={() => setCategories((prev) => toggleTag(prev, cat))}
                        className={cn(
                          "rounded-full border px-3 py-1.5 text-xs font-medium transition-colors",
                          categories.includes(cat)
                            ? "border-pink-500/30 bg-pink-500/20 text-pink-300"
                            : "border-border bg-bg-surface text-muted-foreground",
                        )}
                      >
                        {cat}
                      </button>
                    ))}
                  </div>
                  {canSave && (
                    <button
                      type="button"
                      onClick={() => void handleSavePreferences()}
                      disabled={isSaving}
                      className="flex w-full items-center justify-center gap-2 rounded-xl border border-pink-500/20 bg-pink-500/15 py-3 text-sm font-medium text-pink-300 disabled:opacity-60"
                    >
                      {isSaving && <Loader2 className="h-4 w-4 animate-spin" />}
                      {t("settings.save")}
                    </button>
                  )}
                  {(saveError || prefsStatus) && (
                    <p
                      className={cn(
                        "mt-4 text-center text-xs",
                        saveError ? "text-red-400/70" : "text-muted-foreground",
                      )}
                    >
                      {saveError instanceof Error ? saveError.message : prefsStatus}
                    </p>
                  )}
                </>
              )}
            </div>
          )}

        </div>
      )}

      <SquadDissolveFlow
        squadId={dissolveSquadId}
        open={dissolveSquadId !== null}
        onOpenChange={(next) => {
          if (!next) setDissolveSquadId(null);
        }}
        userId={user?.id}
        onComplete={() => {
          setDissolveSquadId(null);
          setExpandedSquadId(null);
        }}
      />

      <SquadLeaveFlow
        squadId={leaveSquadId}
        open={leaveSquadId !== null}
        onOpenChange={(next) => {
          if (!next) setLeaveSquadId(null);
        }}
        userId={user?.id}
        onComplete={() => {
          setLeaveSquadId(null);
          setExpandedSquadId(null);
        }}
      />

      <FeedbackSheet open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />

      {user?.email && (
        <DeleteAccountModal
          open={deleteAccountOpen}
          onOpenChange={setDeleteAccountOpen}
          userEmail={user.email}
          onConfirmDelete={handleDeleteAccount}
        />
      )}

      {user?.id && !isGuest && (
        <AboModal open={aboModalOpen} onOpenChange={setAboModalOpen} userId={user.id} />
      )}
    </div>
  );
}
